import { View, Pressable } from 'react-native'
import { ErpOrderHistoryItem, getErpDate } from '@ca/common/types'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import Gradient from '@ui/effects/Gradient'
import { colors } from '@ui/colors'
import { colorBackground, colorPressed } from '@ui/effects/Highlights/styles'
import { styleImage } from '@frame/Frame/styles'
import { GradientPoints } from '@ui/effects/Gradient/types'
import { gap } from '@ui/theme/values'
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated'
import Border from '@ui/effects/Border'
import useRWD from '@ui/theme/useRWD'
import Typography from '@ui/typography'
import Chip from '@ui/elements/Chip'
import { format } from 'date-fns'
import { ChipProps } from '@ui/elements/Chip/types'
import cn from '@ui/utils/cn'
import { Image } from 'expo-image'
import { borderRadius, height } from './styles'
import {
  OrderPaymentOverviewBadge,
  OrderStatusBadge,
  useOrderDetailsPayment,
} from './OrderBadge'
import { useConfigConditionValue } from 'features/config/useConfigConditionValue'
import { ORDER_HISTORY } from 'features/config/ConfigConditionSchemas'

interface Props {
  data: ErpOrderHistoryItem
  onPress?: () => void
}

const gradientPoints: GradientPoints = {
  start: [0, 0.5],
  end: [1, 0.5],
}

export default function OrderHistoryTile({ data, onPress }: Props) {
  const {
    animation: animationPress,
    onPressIn,
    onPressOut,
  } = useAnimatedPress()
  const { screen } = useRWD()
  const Container = !onPress ? View : Pressable

  const orderHistory = useConfigConditionValue(ORDER_HISTORY, 'orderHistory')
  const showPayments = orderHistory?.payments
  const { display: displayPrice } = useOrderDetailsPayment(data)

  const styleContainer = {
    borderRadius,
    minWidth: screen.width - 2 * gap,
    minHeight: height,
  }

  const styleDetails: ChipProps = {
    styleText: {
      color: colors.text50,
    },
    styleContainer: {
      borderColor: colors.text25,
    },
  }

  const styleBackgroundPressed = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animationPress.value, [0, 1], [0, 1]),
    }
  })

  const image = data.frame.url

  return (
    <Container
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      onPress={onPress}
      className="relative inset-0 flex h-max flex-1 flex-col"
      style={styleContainer}
      // disabled={isDisabled}
    >
      <Gradient
        colors={colorBackground.default}
        style={{ borderRadius }}
        {...gradientPoints}
      />
      <Gradient
        colors={colorPressed.default}
        style={[styleBackgroundPressed, { borderRadius }]}
        {...gradientPoints}
      />
      <Border style={{ borderRadius }} />
      <View
        style={[styleContainer, { height }]}
        className="relative inset-0 flex flex-row items-center justify-between pl-12 pr-16"
      >
        <View className="flex flex-col items-start gap-4">
          <View className="flex flex-row items-center gap-2">
            <OrderStatusBadge data={data} />
            {showPayments && <OrderPaymentOverviewBadge data={data} />}
          </View>
          <View className="flex flex-row items-center gap-4">
            <Typography variant="LGB">{data.ref}</Typography>
          </View>
          <View className="flex flex-row items-center gap-2">
            <Chip
              {...styleDetails}
              label={format(getErpDate(data.createdAt), 'dd.MM.yyyy')}
            />
            <Chip {...styleDetails} label={displayPrice} />
            <Chip {...styleDetails} label={data.config.strategy_ref} />
          </View>
        </View>
        {!!image && (
          <Animated.View
            className={cn('h-full justify-center')}
            style={[styleImage]}
          >
            <Image
              transition={200}
              contentFit="contain"
              source={image}
              style={styleImage}
            />
          </Animated.View>
        )}
      </View>
    </Container>
  )
}
